import { v4 as uuid } from 'uuid';
import type {
  RegistrationBrowser,
  RegistrationOs,
  RegistrationSource,
} from 'src/hooks/useInitiateNewPatientRegistration';

const STORAGE_KEY = 'boulderDeviceId';

/**
 * Returns the identifier shared by requests from this browser installation.
 * It is stored in localStorage so it survives individual registration sessions.
 */
export function getDeviceId(): string {
  if (typeof window === 'undefined') {
    return uuid();
  }

  try {
    const storedDeviceId = window.localStorage.getItem(STORAGE_KEY);

    if (storedDeviceId) {
      return storedDeviceId;
    }

    const deviceId = uuid();
    window.localStorage.setItem(STORAGE_KEY, deviceId);

    return deviceId;
  } catch {
    return uuid();
  }
}

/** Returns the registration source based on the browser's user agent. */
export function getBrowserCategory(): RegistrationSource {
  return /Mobi/.test(window.navigator.userAgent) ? 'MOBILE_WEB' : 'NATIVE_WEB';
}

/** Returns the registration browser based on the browser's user agent. */
export function getBrowserName(): RegistrationBrowser {
  const { userAgent } = window.navigator;

  if (!userAgent) {
    return 'UNKNOWN';
  }
  if (/Edg\/|EdgA\/|EdgiOS\//.test(userAgent)) {
    return 'EDGE';
  }
  if (/Chrome\/|CriOS\//.test(userAgent)) {
    return 'CHROME';
  }
  if (/Firefox\/|FxiOS\//.test(userAgent)) {
    return 'FIREFOX';
  }
  if (/Safari\//.test(userAgent)) {
    return 'SAFARI';
  }
  return 'OTHER';
}

/** Returns the registration operating system based on the browser's user agent. */
export function getDeviceOs(): RegistrationOs {
  const { userAgent, maxTouchPoints } = window.navigator;
  const isMac = /Macintosh/.test(userAgent);

  if (/iPhone|iPad|iPod/.test(userAgent) || (isMac && maxTouchPoints > 1)) {
    return 'IOS';
  }
  if (/Android/.test(userAgent)) {
    return 'ANDROID';
  }
  if (/Windows/.test(userAgent)) {
    return 'WINDOWS';
  }
  if (isMac) {
    return 'MACOS';
  }
  return 'UNKNOWN';
}
